import { lazy, Suspense, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { loadDeferredClerk } from "@/lib/deferredClerk";
import { cfImage } from "@/lib/utils";
import { AppLogo } from "./AppLogo";
import {
  usePostSignupAthleteCode,
  PostSignupAthleteCodeBanner,
} from "@/components/auth/PostSignupAthleteCodePrompt";
import { Pressable } from "@/components/ui/pressable";
import { getSignInCallbackUrl } from "./navigationAuth";

const AppUserMenu = lazy(() =>
  import("./AppUserMenu").then((module) => ({ default: module.AppUserMenu }))
);
const AuthDialog = lazy(() =>
  import("@/components/auth/AuthDialog").then((module) => ({
    default: module.AuthDialog,
  }))
);

const UserMenuFallback = () => (
  <div className="h-[38px] w-[76px] rounded-md bg-white/20" />
);

const DeferredAppUserMenu = () => (
  <Suspense fallback={<UserMenuFallback />}>
    <AppUserMenu />
  </Suspense>
);

const SignInNavAuthButton = ({ white = false }: { white?: boolean }) => {
  const location = useLocation();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const callbackUrl = getSignInCallbackUrl(location);

  return (
    <>
      <Pressable
        className={`${white ? "text-white border-white" : "text-tenant-secondary border-tenant-accent"} text-xl hover:underline hover:underline-offset-2 uppercase tracking-wide bg-transparent cursor-pointer border-2 rounded-md px-2 py-1 box-border font-semibold`}
        data-signin-trigger="true"
        onClick={() => {
          loadDeferredClerk();
          setAuthDialogOpen(true);
        }}
      >
        Sign In
      </Pressable>
      {authDialogOpen && (
        <Suspense fallback={null}>
          <AuthDialog
            trigger={<span className="hidden" />}
            initialView="signIn"
            isOpen={authDialogOpen}
            onOpenChange={setAuthDialogOpen}
            callbackUrl={callbackUrl}
          />
        </Suspense>
      )}
    </>
  );
};

/**
 * Main navigation component.
 * On sign-in page: compact bar with small logo, centered tagline, and SIGN IN button.
 * On other pages: full nav with logo and user menu.
 */
export const Navigation = () => {
  const { tenant, tenantId } = useTenant();
  const location = useLocation();
  const isWvu = tenantId === "wvu";
  const isFsu = tenantId === "fsu";
  const isGeorgiaSouthern = tenantId === "georgiasouthern";
  const signInNavLogo = isGeorgiaSouthern
    ? tenant.branding?.logos?.horizontal
    : tenant.branding?.logos?.small;
  const isSignInPage = location.pathname === "/auth/signin" || location.pathname.startsWith("/auth/signin/");
  const showAthleteCodeBanner = usePostSignupAthleteCode();

  // ── Sign-in page: compact centered nav ──
  if (isSignInPage) {
    return (
      <nav
        id="signin-nav"
        className="isolate absolute left-0 right-0 z-30 w-full px-4 sm:px-8 md:px-12 py-2 sm:py-4 backdrop-blur-sm backdrop-saturate-150 border-b border-white/15"
        style={{
          backgroundColor: `${tenant.colors.primary}d9`,
          top: "var(--degraded-service-banner-height, 0px)",
        }}
      >
        {/* Solid color backing to strengthen the tint */}
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{ backgroundColor: tenant.colors.primary }}
        />
        {/* Scanline texture overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "linear-gradient(transparent 96%, rgba(255,255,255,0.6) 97%)",
            backgroundSize: "100% 4px",
          }}
        />
        <div className="relative z-10 flex items-center justify-between">
          {/* Left — Small logo */}
          <Link id="signin-nav-logo" to="/auth/signin" className="shrink-0">
            {signInNavLogo ? (
              <img
                src={cfImage(signInNavLogo, { width: isGeorgiaSouthern ? 240 : 80, quality: 90 })}
                alt={isGeorgiaSouthern ? "GATA Travel" : tenant.branding.siteTitle || "Logo"}
                width={isGeorgiaSouthern ? 240 : 40}
                height={40}
                loading="eager"
                decoding="async"
                fetchPriority="high"
                className="block h-8 sm:h-10 w-auto object-contain"
              />
            ) : (
              <span className="text-white font-bold text-lg">
                {tenant.branding?.siteTitle}
              </span>
            )}
          </Link>

          {/* Right — Tagline + Sign In */}
          <div id="signin-nav-right" className="flex items-center gap-4 shrink-0">
            <h2 id="signin-nav-tagline" className="hidden sm:block text-white text-sm md:text-base lg:text-xl font-bold uppercase tracking-wider">
              {tenant.branding?.tagline}
            </h2>
            <SignInNavAuthButton white={isGeorgiaSouthern} />
          </div>
        </div>
      </nav>
    );
  }

  // ── Default nav for authenticated pages ──
  const desktopBgClass = tenant.layout?.navigation?.desktopBackgroundClass ??
    "bg-primary h-auto hidden md:flex flex-col items-end justify-between py-4 px-32";
  const mobileBgClass = tenant.layout?.navigation?.mobileBackgroundClass ??
    "bg-primary h-auto flex flex-row items-center justify-between py-2 px-8 w-full md:hidden";
  const taglineClass = isGeorgiaSouthern
    ? "text-white md:text-3xl md:tracking-wide text-right uppercase"
    : tenant.layout?.navigation?.taglineClass ??
      "text-white md:text-3xl md:tracking-wide text-right uppercase";

  return (
    <>
      {/* Desktop Navigation */}
      <nav className={desktopBgClass}>
        <div className="mx-auto flex w-full items-end justify-end">
          <DeferredAppUserMenu />
        </div>
        <div className="flex flex-row justify-between w-full items-end">
          <div className={`hidden md:block ${isWvu ? "pb-4" : ""} ${tenantId === "byu" || isGeorgiaSouthern ? "shrink-0" : ""}`}>
            <AppLogo
              width={isWvu ? 110 : isGeorgiaSouthern ? 130 : 280}
              height={isWvu || tenantId === "byu" ? undefined : 80}
              variant={isGeorgiaSouthern ? "stacked" : "default"}
              imgClassName={isWvu ? "scale-150 origin-left" : undefined}
            />
          </div>
          {!isFsu && (
            <div className="block md:hidden">
              <AppLogo width={150} height={56} />
            </div>
          )}
          <div className="flex w-full items-center justify-end gap-4 mt-8">
            <h2 className={`${taglineClass}${tenant.id === "unisun" ? " font-bold" : ""}`}>
              {tenant.branding?.tagline}
            </h2>
            {(tenant.id === "byu" || isGeorgiaSouthern) &&
              tenant.branding?.logos?.small && (
                <img
                  src={cfImage(tenant.branding.logos.small, {
                    width: isGeorgiaSouthern ? 140 : 96,
                    quality: 90,
                  })}
                  alt={
                    isGeorgiaSouthern
                      ? "Georgia Southern Eagles"
                      : tenant.branding.siteTitle || "Logo"
                  }
                  className={
                    isGeorgiaSouthern
                      ? "h-14 w-auto object-contain"
                      : "h-12 w-12 object-contain"
                  }
                />
              )}
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className={mobileBgClass}>
        {isWvu ? (
          <>
            <div className="w-full flex items-center">
              <Link to="/">
                <img
                  src={cfImage(tenant.branding?.logos?.horizontal ?? "", { width: 240, quality: 90 })}
                  alt={tenant.branding?.siteTitle || "Logo"}
                  width={240}
                  height={84}
                  className="block max-[375px]:w-[200px] max-[375px]:h-auto"
                />
              </Link>
            </div>
            <div className="absolute right-4 top-3">
              <DeferredAppUserMenu />
            </div>
          </>
        ) : (
          <>
            <div>
              <AppLogo
                width={isGeorgiaSouthern ? 92 : 150}
                height={56}
                variant={isGeorgiaSouthern ? "stacked" : "default"}
              />
            </div>
            <div className="flex flex-col items-end justify-end space-y-2">
              <DeferredAppUserMenu />
              {tenant.branding?.logos?.small && !isFsu && (
                <img
                  src={cfImage(tenant.branding.logos.small, {
                    width: isGeorgiaSouthern ? 140 : 80,
                    quality: 90,
                  })}
                  alt={
                    isGeorgiaSouthern
                      ? "Georgia Southern Eagles"
                      : tenant.branding.siteTitle || "Logo"
                  }
                  width={isGeorgiaSouthern ? 68 : 40}
                  height={40}
                  className="block h-10 w-auto object-contain"
                />
              )}
            </div>
          </>
        )}
      </nav>

      {showAthleteCodeBanner && <PostSignupAthleteCodeBanner />}
    </>
  );
};

export default Navigation;
